import clsx from "clsx";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { type RouteComponentProps, useLocation } from "react-router-dom";

import { useGetChatMediaQuery, useGetChatMessagesQuery, useGetChatThreadsQuery, useMarkChatReadMutation, useSendChatMessageMutation } from "~/api";
import { fetchWithAuth } from "~/common/fetch";
import { ChevronLeft, type IconProps, MessagesSquare, Paperclip, Send, User } from "~/common/redesign-v1/icons";
import { SearchInput } from "~/common/redesign-v1/ui";
import { useOptionalUser } from "~/common/user";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { API_ACCOUNT_AVATAR } from "~/config";
import { setDocumentTitle } from "~/hooks/use-document-title";

import careDepartmentAvatar from "./care-department-avatar.svg";
import { ChatFileDropZone } from "./chat-file-drop-zone";
import { ChatImagePreviewTrigger, ChatVideoExpandButton } from "./chat-image-preview";
import expressSupportAvatar from "./express-support-avatar.svg";
import personalTechnicianFallbackAvatar from "./personal-technician-fallback-avatar.svg";

/** Same typography as `MeetingsLayout` h1 (`meetings.heading`). */
export const chatHeadingClassName = clsx("tw-text-xl tw-font-semibold tw-text-brand-black sm:tw-text-2xl");

export const chatPageHeadingClassName = clsx(chatHeadingClassName, "tw-my-0 tw-mb-6 tw-max-w-fit max-sm:tw-px-1 sm:tw-mt-3");
import { clearChatComposeDraft, readChatComposeDraft, writeChatComposeDraft } from "./chat-compose-draft";
import { chatFileKind, ChatFileThumbnail, ChatThreadTabButtons, resolveChatFileUrl } from "./chat-files";
import { chatFileMatchesThreadSearch, chatMessageMatchesThreadSearch, normalizeChatThreadSearchQuery } from "./chat-thread-search";
import { filterVisibleChatThreads, isStaffChannelComposeLocked, sortChatThreadsByChannelOrder } from "./chat-thread-visibility";
import { useChatComposeAutogrow } from "./use-chat-compose-autogrow";
import { useChatMessagesAutoScroll } from "./use-chat-messages-scroll";
import { useChatThreadAllMessages } from "./use-chat-thread-all-messages";

export type TChannel = "express_support" | "personal_technician" | "personal_manager" | "care_department";

export function channelTitleId(channel: TChannel) {
  return `chat.channel.${channel}` as const;
}

export function channelSubtitleId(channel: TChannel) {
  return `chat.channel.${channel}.subtitle` as const;
}

export function channelDescriptionId(channel: TChannel) {
  return `chat.channel.${channel}.description` as const;
}

import { chatFileMonthKey, formatChatBubbleTime as formatBubbleTime, formatChatDateSeparator, formatChatFileMonth, formatChatThreadListTime, messageDayKey } from "./chat-datetime";

type ChatMediaFile = { id: number; file_name: string; mime_type: string | null; get_url: string | null; created_at: string };

/** Group chat files into month sections (newest month first) for the files view (frame 5289-123835). */
function groupChatFilesByMonth(files: ChatMediaFile[]): { key: string; files: ChatMediaFile[] }[] {
  const groups = new Map<string, ChatMediaFile[]>();
  for (const f of files) {
    const key = chatFileMonthKey(f.created_at) || "unknown";
    const bucket = groups.get(key);
    if (bucket) bucket.push(f);
    else groups.set(key, [f]);
  }
  return [...groups.entries()].sort((a, b) => b[0].localeCompare(a[0])).map(([key, groupFiles]) => ({ key, files: groupFiles }));
}

export { formatBubbleTime, formatChatDateSeparator, formatChatThreadListTime, messageDayKey };

export function ChatDateSeparator({ label }: { label: string }) {
  return (
    <div className="tw-flex tw-w-full tw-shrink-0 tw-justify-center tw-py-1" role="separator" aria-label={label}>
      <span className="tw-inline-flex tw-rounded-full tw-bg-[#6b7280]/90 tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-leading-snug tw-text-white tw-shadow-sm">{label}</span>
    </div>
  );
}

export type ThreadRow = {
  id: number;
  channel: TChannel;
  unread_count: number;
  assignee?: null | {
    account_id: number;
    first_name: string;
    last_name: string;
    middle_name: string;
    avatar: string | null;
  };
  last_message: null | { text: string; created_at: string | null };
};

/** Uses the same last-name/first-name initials as the account avatar. The third argument is ignored. */
export function initialsFromPerson(last?: string | null, first?: string | null, _?: string | null): string {
  const parts = [last, first].map((s) => (s || "").trim()).filter(Boolean);
  if (parts.length === 0) return "";
  return parts
    .map((s) => {
      const ch = [...s][0];
      return ch ? ch.toUpperCase() : "";
    })
    .join("")
    .slice(0, 2);
}

export function assigneeFullName(assignee: NonNullable<ThreadRow["assignee"]>, locale: string): string {
  const ln = (assignee.last_name || "").trim();
  const fn = (assignee.first_name || "").trim();
  const mn = (assignee.middle_name || "").trim();
  if (!ln && !fn && !mn) return "";
  const loc = locale.toLowerCase();
  if (loc.startsWith("ru")) {
    return [ln, fn, mn].filter(Boolean).join(" ");
  }
  return [fn, mn, ln].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
}

/** Assignee name for personal technician/manager thread headers (role subtitle is always shown separately). */
export function channelThreadAssigneeName(channel: TChannel, assignee: ThreadRow["assignee"], locale: string): string {
  if (!assignee || (channel !== "personal_technician" && channel !== "personal_manager")) {
    return "";
  }
  return assigneeFullName(assignee, locale);
}

export function ChatThreadHeaderSubtitle({
  channel,
  assignee,
  locale,
  className,
  nameClassName,
  subtitleClassName,
  descriptionClassName,
  hideDescription = false,
}: {
  channel: TChannel;
  assignee: ThreadRow["assignee"];
  locale: string;
  className?: string;
  nameClassName?: string;
  subtitleClassName?: string;
  descriptionClassName?: string;
  hideDescription?: boolean;
}) {
  const assigneeName = channelThreadAssigneeName(channel, assignee, locale);

  return (
    <div className={clsx("tw-flex tw-flex-col tw-gap-0.5", className)}>
      {assigneeName ? <div className={clsx("tw-whitespace-normal tw-break-words tw-text-[11px] tw-font-semibold tw-leading-tight tw-text-brand-black", nameClassName)}>{assigneeName}</div> : null}
      {!assigneeName ? (
        <div className={clsx("tw-whitespace-normal tw-break-words tw-text-[11px] tw-font-semibold tw-leading-tight tw-text-brand-gray", subtitleClassName)}>
          <FormattedMessage id={channelSubtitleId(channel)} />
        </div>
      ) : null}
      {!hideDescription ? (
        <div className={clsx("tw-whitespace-normal tw-break-words tw-text-[11px] tw-font-normal tw-leading-snug tw-text-[#9CA3AF]", descriptionClassName)}>
          <FormattedMessage id={channelDescriptionId(channel)} />
        </div>
      ) : null}
    </div>
  );
}

/** Lab message avatar: prefer message author, else thread assignee (configured specialist). */
export function resolveLabMessageAvatar(
  message: {
    sender_type: "doctor" | "lab";
    sender_account_id?: number;
    sender_avatar?: string | null;
    sender_first_name?: string;
    sender_last_name?: string;
    sender_middle_name?: string;
  },
  assignee: ThreadRow["assignee"],
): { accountId: number; avatarFilename: string | null | undefined; initials: string } | null {
  if (message.sender_type !== "lab") {
    return null;
  }

  const accountId = message.sender_account_id ?? assignee?.account_id ?? 0;
  if (accountId <= 0) {
    return null;
  }

  const avatarFilename = message.sender_avatar != null && String(message.sender_avatar).trim() !== "" ? message.sender_avatar : assignee?.avatar;

  const initials = initialsFromPerson(message.sender_last_name ?? assignee?.last_name, message.sender_first_name ?? assignee?.first_name, message.sender_middle_name ?? assignee?.middle_name) || "?";

  return { accountId, avatarFilename, initials };
}

export function ChatRoundAvatar({
  accountId,
  avatarFilename,
  initials,
  size = "lg",
  className,
}: {
  accountId: number;
  avatarFilename: string | null | undefined;
  initials: string;
  size?: "lg" | "md" | "sm";
  className?: string;
}) {
  const [broken, setBroken] = useState(false);
  const safeInitials = initials.trim() || "?";
  const url = avatarFilename && String(avatarFilename).trim() !== "" && !broken && accountId > 0 ? API_ACCOUNT_AVATAR(accountId) : undefined;
  const dims = size === "lg" ? "tw-h-12 tw-w-12 tw-text-[11px] tw-leading-none" : size === "md" ? "tw-h-10 tw-w-10 tw-text-sm tw-leading-none" : "tw-h-9 tw-w-9 tw-text-[10px] tw-leading-none";

  React.useEffect(() => {
    setBroken(false);
  }, [accountId, avatarFilename]);

  const showPhoto = Boolean(url && !broken);
  // Non-photo avatars are neutral grey (frames 5284-117324 / 5289-123835), not a colour palette.
  const surfaceClass = showPhoto ? "tw-border tw-border-[#E8EAED] tw-bg-white tw-text-brand-black" : "tw-bg-[#EFEDEB] tw-font-semibold tw-tracking-tight tw-text-[#57534E]";

  return (
    <div className={clsx("tw-relative tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg", surfaceClass, dims, className)}>
      {url ? <img src={url} alt="" className="tw-absolute tw-inset-0 tw-size-full tw-object-cover" crossOrigin="use-credentials" onError={() => setBroken(true)} /> : null}
      {!url || broken ? <span className="tw-relative tw-z-[1] tw-select-none">{safeInitials}</span> : null}
    </div>
  );
}

// These channels have fixed identities independent of the staff account assigned to the ticket.
const CHANNEL_AVATAR: Partial<Record<TChannel, string>> = {
  express_support: expressSupportAvatar,
  care_department: careDepartmentAvatar,
};

// Channel illustration used when no assignee photo is available.
const CHANNEL_NO_ASSIGNEE_AVATAR: Partial<Record<TChannel, string>> = {
  personal_technician: personalTechnicianFallbackAvatar,
};

// Fallback when neither an assignee nor a configured manager can identify the channel.
const CHANNEL_ICON: Partial<Record<TChannel, React.ComponentType<IconProps>>> = {
  personal_manager: User,
};

export function ThreadListAvatar({ channel, assignee, size = "lg" }: { channel: TChannel; assignee: ThreadRow["assignee"]; size?: "lg" | "md" | "sm" }) {
  const user = useOptionalUser();
  const boxClass = size === "sm" ? "tw-h-9 tw-w-9" : size === "md" ? "tw-h-10 tw-w-10" : "tw-h-12 tw-w-12";

  // Fixed channel identity takes precedence over the backend's internal ticket assignee.
  const avatarSrc = CHANNEL_AVATAR[channel];
  if (avatarSrc) {
    return (
      <div className={clsx("tw-shrink-0 tw-overflow-hidden tw-rounded-lg tw-border tw-border-[#E8EAED] tw-bg-white", boxClass)}>
        <img src={avatarSrc} alt="" className="tw-size-full tw-object-cover" />
      </div>
    );
  }

  const hasAssigneePhoto = Boolean(assignee?.avatar && String(assignee.avatar).trim() !== "" && assignee.avatar !== "None");
  const noAssigneeAvatar = CHANNEL_NO_ASSIGNEE_AVATAR[channel];

  // Prefer the channel illustration to assignee initials when that illustration exists and no photo does.
  if (assignee && (hasAssigneePhoto || !noAssigneeAvatar)) {
    const ini = initialsFromPerson(assignee.last_name, assignee.first_name, assignee.middle_name);
    return <ChatRoundAvatar accountId={assignee.account_id} avatarFilename={assignee.avatar} initials={ini || "?"} size={size} />;
  }

  // No assignee yet on the personal-manager thread: show the doctor's OWN manager (name only, same
  // `TUser.manager` field the sidebar card reads) instead of a generic building icon, so this avatar
  // never contradicts the one in the sidebar.
  if (channel === "personal_manager" && !assignee && user?.manager) {
    const ini = initialsFromPerson(user.manager.last_name, user.manager.first_name, user.manager.middle_name);
    return <ChatRoundAvatar accountId={0} avatarFilename={null} initials={ini || "?"} size={size} />;
  }

  if (noAssigneeAvatar) {
    return (
      <div className={clsx("tw-shrink-0 tw-overflow-hidden tw-rounded-lg tw-border tw-border-[#E8EAED] tw-bg-white", boxClass)}>
        <img src={noAssigneeAvatar} alt="" className="tw-size-full tw-object-cover" />
      </div>
    );
  }

  const Icon = CHANNEL_ICON[channel];
  const iconClass = size === "sm" ? "tw-h-5 tw-w-5" : size === "md" ? "tw-h-5 tw-w-5" : "tw-h-6 tw-w-6";
  return (
    <div className={clsx("tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-canvas-accent tw-bg-[#EFEDEB] tw-text-[#57534E]", boxClass)}>
      {Icon ? <Icon className={clsx(iconClass, "tw-text-current")} aria-hidden /> : null}
    </div>
  );
}

export function threadPreviewText(t: ThreadRow, intl: ReturnType<typeof useIntl>): string {
  if (!t.last_message) return intl.formatMessage({ id: "chat.thread.empty_preview" });
  const text = t.last_message.text?.trim();
  if (text) return text;
  return intl.formatMessage({ id: "chat.thread.file_only" });
}

export default function ChatPage({ bareLayout = false }: { bareLayout?: boolean } & Partial<RouteComponentProps>) {
  const intl = useIntl();
  const location = useLocation();
  React.useEffect(() => {
    setDocumentTitle(intl.formatMessage({ id: "chat.title" }));
  }, [intl]);

  const { data, isLoading, isError } = useGetChatThreadsQuery(undefined, { pollingInterval: 5000 });
  const threads = data?.threads ?? [];
  const visibleThreads = useMemo(() => sortChatThreadsByChannelOrder(filterVisibleChatThreads(threads)), [threads]);
  const requestedChannel = new URLSearchParams(location.search).get("channel");

  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null);
  const [threadSearchQuery, setThreadSearchQuery] = useState("");
  const [threadSearchResultCount, setThreadSearchResultCount] = useState<number | null>(null);
  const [mediaFilesCount, setMediaFilesCount] = useState(0);
  const [activeTab, setActiveTab] = useState<"chat" | "media">("chat");
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const [threadListCollapsed, setThreadListCollapsed] = useState(false);
  const appliedRequestedChannel = useRef<string | null>(null);

  const selectThread = React.useCallback((id: number) => {
    setSelectedThreadId(id);
    setActiveTab("chat");
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 1199px)").matches) {
      setMobileThreadOpen(true);
    }
  }, []);

  React.useEffect(() => {
    const mq = window.matchMedia("(min-width: 1200px)");
    const sync = () => {
      if (mq.matches) setMobileThreadOpen(false);
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const selectedThread = useMemo(() => {
    if (selectedThreadId != null) {
      const byId = visibleThreads.find((t) => t.id === selectedThreadId);
      if (byId) return byId;
    }
    return visibleThreads[0] ?? null;
  }, [visibleThreads, selectedThreadId]);

  React.useEffect(() => {
    if (visibleThreads.length === 0) return;
    if (requestedChannel && appliedRequestedChannel.current !== requestedChannel) {
      appliedRequestedChannel.current = requestedChannel;
      const requested = visibleThreads.find((thread) => thread.channel === requestedChannel);
      if (requested) {
        setSelectedThreadId(requested.id);
        // Deep-linking a channel (e.g. the manager card «Написать» → ?channel=personal_manager) must OPEN the
        // conversation on mobile too — otherwise mobile opens the thread list with the row merely highlighted
        // and no messages/composer/back button (the single-pane mobile chat only shows messages when
        // `mobileThreadOpen`). Desktop-lg is unaffected (both panes always visible).
        if (typeof window !== "undefined" && window.matchMedia("(max-width: 1199px)").matches) {
          setMobileThreadOpen(true);
        }
        return;
      }
    }
    if (selectedThreadId == null) {
      const first = visibleThreads[0];
      if (first) setSelectedThreadId(first.id);
      return;
    }
    if (!visibleThreads.some((t) => t.id === selectedThreadId)) {
      const first = visibleThreads[0];
      if (first) setSelectedThreadId(first.id);
    }
  }, [requestedChannel, selectedThreadId, visibleThreads]);

  React.useEffect(() => {
    setActiveTab("chat");
  }, [selectedThread?.id]);

  React.useEffect(() => {
    setThreadSearchQuery("");
    setThreadSearchResultCount(null);
  }, [selectedThread?.id]);

  return (
    <Layout chrome={!bareLayout} contentClassName="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-visible">
      {isLoading ? <Loader /> : null}
      {isError ? <div className="tw-p-4">Ошибка загрузки</div> : null}

      {!isLoading && !isError ? (
        // Keep page scrolling disabled: the thread list and messages own their scroll areas.
        <div className="tw-flex tw-min-h-0 tw-w-full tw-min-w-0 tw-flex-1 tw-flex-col">
          {/* Chat owns this inset because the full-height shell omits card padding. It matches the standard
              page-title inset and centers the title on mobile. */}
          <h1 className="rd-page-title tw-m-0 tw-shrink-0 tw-px-4 tw-py-3 tw-font-rd-accent tw-text-foreground max-[990px]:tw-text-center desktop:tw-py-6">
            <FormattedMessage id="chat.title" />
          </h1>

          {/* The chat panels fill the rest of the SAME white card — NO nested card chrome (removing the
              double-card that broke the layout, Figma 7221-19085 = ONE card). `rd-chat-ground` stays `bg-white`
              as the flag-off fallback; on desktop the shell card supplies the white + rounding, on mobile the
              whole shell column is white. Panels are flush to the card edges so the thread-list divider + header
              border span the full card width (per the frame). */}
          <div className="rd-chat-ground tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-overflow-hidden tw-bg-white desktop:tw-rounded-b-xl">
            {selectedThread ? (
              <>
                <div
                  className={clsx(
                    "tw-hidden tw-h-full tw-min-h-0 tw-flex-1 desktop-lg:tw-grid desktop-lg:tw-grid-rows-[auto_minmax(0,1fr)]",
                    threadListCollapsed ? "desktop-lg:tw-grid-cols-[56px_minmax(0,1fr)]" : "desktop-lg:tw-grid-cols-[320px_minmax(0,1fr)]",
                  )}
                >
                  {/* Both tabs use the same header container; only their content differs. */}
                  <div className="tw-rounded-tr-lg tw-border-b tw-border-[#E1E4ED] tw-bg-[#f5f5f5] tw-p-4 desktop-lg:tw-col-start-2 desktop-lg:tw-row-start-1">
                    {activeTab !== "media" ? (
                      <ChatDesktopHeaderTitle
                        channel={selectedThread.channel}
                        assignee={selectedThread.assignee ?? null}
                        activeTab={activeTab}
                        onActiveTabChange={setActiveTab}
                        unreadCount={selectedThread.unread_count ?? 0}
                        threadSearchQuery={threadSearchQuery}
                        onThreadSearchQueryChange={setThreadSearchQuery}
                        threadListCollapsed={threadListCollapsed}
                        onToggleThreadList={() => setThreadListCollapsed((v) => !v)}
                      />
                    ) : (
                      <ChatDesktopFilesHeaderTitle
                        fileCount={mediaFilesCount}
                        activeTab={activeTab}
                        onActiveTabChange={setActiveTab}
                        threadSearchQuery={threadSearchQuery}
                        onThreadSearchQueryChange={setThreadSearchQuery}
                      />
                    )}
                  </div>
                  {/* Header-left panel (Figma 7221:19697, empty in this state): grey fill + bottom border,
                      level with the header on the right, rounded top-left to match the card corner. The
                      column divider (`border-r`) continues through it so the vertical rule is unbroken. */}
                  <div className="tw-flex tw-items-center tw-justify-center tw-rounded-tl-lg tw-border-b tw-border-[#E1E4ED] tw-bg-[#f5f5f5] desktop-lg:tw-col-start-1 desktop-lg:tw-row-start-1 desktop-lg:tw-border-r desktop-lg:tw-border-[#E1E4ED]">
                    {threadListCollapsed ? (
                      <button
                        type="button"
                        onClick={() => setThreadListCollapsed(false)}
                        aria-label={intl.formatMessage({ id: "chat.thread_list.expand" })}
                        className="tw-flex tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-2 tw-text-[11px] tw-font-semibold tw-leading-tight tw-text-brand-black tw-outline-none [writing-mode:vertical-rl] hover:tw-opacity-70"
                      >
                        <FormattedMessage id="chat.thread_list.all" />
                      </button>
                    ) : null}
                  </div>
                  <div
                    className={clsx(
                      "tw-flex tw-min-h-0 tw-flex-col tw-overflow-hidden tw-rounded-bl-lg desktop-lg:tw-col-start-1 desktop-lg:tw-row-start-2 desktop-lg:tw-border-r desktop-lg:tw-border-[#E1E4ED]",
                      threadListCollapsed && "tw-invisible",
                    )}
                  >
                    <ChatThreadsList threads={visibleThreads} selectedThreadId={selectedThread.id} onSelect={selectThread} />
                  </div>
                  <div className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-br-lg desktop-lg:tw-col-start-2 desktop-lg:tw-row-start-2">
                    <ChatThreadView
                      threadId={selectedThread.id}
                      channel={selectedThread.channel}
                      assignee={selectedThread.assignee ?? null}
                      threadListHadMessage={Boolean(selectedThread.last_message)}
                      activeTab={activeTab}
                      onActiveTabChange={setActiveTab}
                      threadSearchQuery={threadSearchQuery}
                      onThreadSearchQueryChange={setThreadSearchQuery}
                      onThreadSearchResultCount={setThreadSearchResultCount}
                      hideOwnFilesHeader
                      onMediaFilesCount={setMediaFilesCount}
                    />
                  </div>
                </div>

                <div className={clsx("tw-flex tw-h-full tw-min-h-0 tw-flex-1 tw-flex-col desktop-lg:tw-hidden", mobileThreadOpen ? "tw-hidden" : null)}>
                  {/* Figma 5295-131952: the SELECTION view sits on the WARM app ground (same as the body) —
                      support card + threads sit on it, NOT white. Mobile = transparent (warm shows); the
                      support band + selected item keep their own tint. */}
                  <div className="rd-chat-ground tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-bg-white">
                    <ChatSupportBand />
                    <ChatThreadsList threads={visibleThreads} selectedThreadId={selectedThread.id} onSelect={selectThread} />
                  </div>
                </div>

                <div className={clsx("tw-flex tw-h-full tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden desktop-lg:tw-hidden", !mobileThreadOpen ? "tw-hidden" : null)}>
                  {/* Figma mobile (5295-132182): NO separate "Назад" row — an inline `‹` chevron sits left of the
                      thread title inside the header itself. `onBack` renders it (mobile only). */}
                  {/* The Files tab supplies its own mobile header, so hide the channel header there. */}
                  {activeTab !== "media" ? (
                    <div className="rd-chat-ground tw-shrink-0 tw-border-b tw-border-[#E1E4ED] tw-bg-[#f5f5f5] tw-p-4">
                      <ChatHeaderTitle
                        channel={selectedThread.channel}
                        assignee={selectedThread.assignee ?? null}
                        activeTab={activeTab}
                        onActiveTabChange={setActiveTab}
                        unreadCount={selectedThread.unread_count ?? 0}
                        threadSearchQuery={threadSearchQuery}
                        onThreadSearchQueryChange={setThreadSearchQuery}
                        threadSearchResultCount={threadSearchResultCount}
                        onBack={() => setMobileThreadOpen(false)}
                      />
                    </div>
                  ) : null}
                  <div className="rd-chat-ground tw-min-h-0 tw-flex-1 tw-overflow-hidden tw-bg-white">
                    <ChatThreadView
                      threadId={selectedThread.id}
                      channel={selectedThread.channel}
                      assignee={selectedThread.assignee ?? null}
                      threadListHadMessage={Boolean(selectedThread.last_message)}
                      activeTab={activeTab}
                      onActiveTabChange={setActiveTab}
                      threadSearchQuery={threadSearchQuery}
                      onThreadSearchQueryChange={setThreadSearchQuery}
                      onThreadSearchResultCount={setThreadSearchResultCount}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col desktop-lg:tw-flex-row">
                <div className="tw-flex tw-min-h-0 tw-w-full tw-flex-col desktop-lg:tw-w-80 desktop-lg:tw-shrink-0 desktop-lg:tw-border-r desktop-lg:tw-border-[#E1E4ED]">
                  <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
                    <ChatThreadsList threads={visibleThreads} selectedThreadId={selectedThreadId} onSelect={selectThread} />
                  </div>
                </div>
                <div className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-items-center tw-justify-center tw-border-t tw-border-[#E1E4ED] tw-p-8 tw-text-sm tw-text-brand-gray desktop-lg:tw-border-t-0">
                  <FormattedMessage id="chat.thread.empty_preview" />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </Layout>
  );
}

function ChatHeaderTitle({
  channel,
  assignee,
  activeTab,
  onActiveTabChange,
  unreadCount,
  threadSearchQuery,
  onThreadSearchQueryChange,
  onBack,
}: {
  channel: TChannel;
  assignee: ThreadRow["assignee"];
  activeTab: "chat" | "media";
  onActiveTabChange: (t: "chat" | "media") => void;
  unreadCount: number;
  threadSearchQuery: string;
  onThreadSearchQueryChange: (value: string) => void;
  threadSearchResultCount: number | null;
  /** Mobile only — renders an inline `‹` back chevron left of the thread title (Figma 5295-132182). */
  onBack?: () => void;
}) {
  const intl = useIntl();
  const threadSearchActive = threadSearchQuery.trim().length > 0;

  // The SHARED redesign SearchInput (one search component across patients / education / chat). Rendered in two
  // responsive slots (inline PC header vs its own line on narrow) — a small helper avoids duplicating the props.
  const searchField = (wrapperClassName: string, testId: string) => (
    <SearchInput
      wrapperClassName={wrapperClassName}
      value={threadSearchQuery}
      onChange={(e) => onThreadSearchQueryChange(e.currentTarget.value)}
      onClear={() => onThreadSearchQueryChange("")}
      onKeyDown={(e) => {
        if (e.key === "Escape") onThreadSearchQueryChange("");
      }}
      placeholder={intl.formatMessage({ id: "chat.thread.search.placeholder" })}
      clearAriaLabel={intl.formatMessage({ id: "chat.thread.search.clear" })}
      aria-label={intl.formatMessage({ id: "chat.thread.search.placeholder" })}
      data-testid={testId}
    />
  );

  return (
    <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-1">
      <div className="tw-flex tw-min-w-0 tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-2">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label={intl.formatMessage({ id: "chat.mini.back" })}
              className="tw-mt-0.5 tw-flex tw-size-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-[#6B7280] tw-outline-none hover:tw-text-brand-black focus-visible:tw-ring-2 focus-visible:tw-ring-brand-green/40"
            >
              <ChevronLeft className="tw-size-6" aria-hidden />
            </button>
          ) : null}
          <ThreadListAvatar channel={channel} assignee={assignee} size="sm" />
          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
            <div className="tw-whitespace-normal tw-break-words tw-text-[13px] tw-font-semibold tw-leading-tight tw-text-brand-black">
              <FormattedMessage id={channelTitleId(channel)} />
            </div>
            <ChatThreadHeaderSubtitle channel={channel} assignee={assignee} locale={intl.locale} hideDescription={threadSearchActive} />
          </div>
          {unreadCount > 0 ? (
            <span
              className="tw-flex tw-h-7 tw-min-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary tw-px-2 tw-text-[11px] tw-font-bold tw-text-primary-foreground"
              aria-label={intl.formatMessage({ id: "chat.unread_total" }, { count: unreadCount })}
              title={intl.formatMessage({ id: "chat.unread_total" }, { count: unreadCount })}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          ) : null}
        </div>
        {/* Right cluster: PC search INLINE, right-aligned, BEFORE the files/chat toggle, at the shared
            patient-table width (310px) — Figma 7221-19085. On narrow (< desktop-lg, the single-pane mobile
            chat) it drops to its own full-width line below (rendered once more, hidden here). */}
        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
          {searchField("tw-hidden min-[1370px]:tw-block min-[1370px]:tw-w-[310px]", "rd-chat-thread-search")}
          <ChatThreadTabButtons activeTab={activeTab} onActiveTabChange={onActiveTabChange} />
        </div>
      </div>
      {/* Below 1370px the search uses a separate row to preserve room for the title. */}
      <div className="tw-space-y-1.5 min-[1370px]:tw-hidden">{searchField("", "rd-chat-thread-search-narrow")}</div>
    </div>
  );
}

/** Desktop header with a control for collapsing the thread list. */
function ChatDesktopHeaderTitle({
  channel,
  assignee,
  activeTab,
  onActiveTabChange,
  unreadCount,
  threadSearchQuery,
  onThreadSearchQueryChange,
  threadListCollapsed,
  onToggleThreadList,
}: {
  channel: TChannel;
  assignee: ThreadRow["assignee"];
  activeTab: "chat" | "media";
  onActiveTabChange: (t: "chat" | "media") => void;
  unreadCount: number;
  threadSearchQuery: string;
  onThreadSearchQueryChange: (value: string) => void;
  threadListCollapsed: boolean;
  onToggleThreadList: () => void;
}) {
  const intl = useIntl();
  const threadSearchActive = threadSearchQuery.trim().length > 0;
  const assigneeName = channelThreadAssigneeName(channel, assignee, intl.locale);

  return (
    <div className="tw-flex tw-w-full tw-min-w-0 tw-items-center tw-gap-6">
      <button
        type="button"
        onClick={onToggleThreadList}
        aria-label={intl.formatMessage({ id: threadListCollapsed ? "chat.thread_list.expand" : "chat.thread_list.collapse" })}
        aria-expanded={!threadListCollapsed}
        className="tw-flex tw-size-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-brand-black tw-outline-none hover:tw-opacity-70 focus-visible:tw-ring-2 focus-visible:tw-ring-brand-green/40"
      >
        <ChevronLeft className={clsx("tw-size-6 tw-transition-transform tw-duration-200", threadListCollapsed && "tw-rotate-180")} aria-hidden />
      </button>
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-2">
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
          <span className="tw-truncate tw-text-sm tw-font-medium tw-leading-5 tw-text-brand-black">
            <FormattedMessage id={channelTitleId(channel)} />
          </span>
          {assigneeName ? (
            <span className="tw-truncate tw-text-sm tw-font-normal tw-leading-5 tw-text-brand-black">{assigneeName}</span>
          ) : (
            <span className="tw-truncate tw-text-sm tw-font-normal tw-leading-5 tw-text-brand-black">
              <FormattedMessage id={channelSubtitleId(channel)} />
            </span>
          )}
          {!threadSearchActive ? (
            // Descriptions wrap because clinical-support copy may span several lines.
            <span className="tw-mt-1 tw-whitespace-normal tw-break-words tw-text-xs tw-font-normal tw-leading-4 tw-text-brand-black">
              <FormattedMessage id={channelDescriptionId(channel)} />
            </span>
          ) : null}
        </div>
        {unreadCount > 0 ? (
          <span
            className="tw-flex tw-h-7 tw-min-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary tw-px-2 tw-text-[11px] tw-font-bold tw-text-primary-foreground"
            aria-label={intl.formatMessage({ id: "chat.unread_total" }, { count: unreadCount })}
            title={intl.formatMessage({ id: "chat.unread_total" }, { count: unreadCount })}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
      </div>
      <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
        <SearchInput
          wrapperClassName="tw-w-[250px]"
          value={threadSearchQuery}
          onChange={(e) => onThreadSearchQueryChange(e.currentTarget.value)}
          onClear={() => onThreadSearchQueryChange("")}
          onKeyDown={(e) => {
            if (e.key === "Escape") onThreadSearchQueryChange("");
          }}
          placeholder={intl.formatMessage({ id: "chat.thread.search.placeholder" })}
          clearAriaLabel={intl.formatMessage({ id: "chat.thread.search.clear" })}
          aria-label={intl.formatMessage({ id: "chat.thread.search.placeholder" })}
          data-testid="rd-chat-thread-search"
        />
        <ChatThreadTabButtons activeTab={activeTab} onActiveTabChange={onActiveTabChange} />
      </div>
    </div>
  );
}

/** Files header matching the desktop conversation header structure. */
function ChatDesktopFilesHeaderTitle({
  fileCount,
  activeTab,
  onActiveTabChange,
  threadSearchQuery,
  onThreadSearchQueryChange,
}: {
  fileCount: number;
  activeTab: "chat" | "media";
  onActiveTabChange: (t: "chat" | "media") => void;
  threadSearchQuery: string;
  onThreadSearchQueryChange: (value: string) => void;
}) {
  const intl = useIntl();

  return (
    <div className="tw-flex tw-w-full tw-min-w-0 tw-items-center tw-gap-6">
      <button
        type="button"
        onClick={() => onActiveTabChange("chat")}
        aria-label={intl.formatMessage({ id: "reminder.back" })}
        className="tw-flex tw-size-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-brand-black tw-outline-none hover:tw-opacity-70 focus-visible:tw-ring-2 focus-visible:tw-ring-brand-green/40"
      >
        <ChevronLeft className="tw-size-6 tw-shrink-0" aria-hidden />
      </button>
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-2">
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
          <span className="tw-truncate tw-text-sm tw-font-medium tw-leading-5 tw-text-brand-black">
            <FormattedMessage id="chat.files.title" defaultMessage="Chat files" />
          </span>
          <span className="tw-truncate tw-text-sm tw-font-normal tw-leading-5 tw-text-brand-black">
            <FormattedMessage id="chat.files.count" defaultMessage="{count, plural, one {# file} other {# files}}" values={{ count: fileCount }} />
          </span>
        </div>
      </div>
      <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
        <SearchInput
          wrapperClassName="tw-w-[250px]"
          value={threadSearchQuery}
          onChange={(e) => onThreadSearchQueryChange(e.currentTarget.value)}
          onClear={() => onThreadSearchQueryChange("")}
          onKeyDown={(e) => {
            if (e.key === "Escape") onThreadSearchQueryChange("");
          }}
          placeholder={intl.formatMessage({ id: "chat.thread.search.placeholder" })}
          clearAriaLabel={intl.formatMessage({ id: "chat.thread.search.clear" })}
          aria-label={intl.formatMessage({ id: "chat.thread.search.placeholder" })}
          data-testid="rd-chat-thread-search"
        />
        <ChatThreadTabButtons activeTab={activeTab} onActiveTabChange={onActiveTabChange} />
      </div>
    </div>
  );
}

/**
 * Support-team caption band — mobile only (5295-131952 "Header right"): a FULL-WIDTH stone-200 band with a
 * bottom border, flush at the top of the white surface. Rendered as a NON-scrolling sibling ABOVE the thread
 * list (NOT inside it) — if it lived inside the `overflow-y-auto` list, the list's vertical scrollbar would
 * shrink the width and the band would trigger a horizontal scrollbar. Full-width needs no negative margin
 * because its parent (the list wrapper) carries no horizontal padding.
 */
function ChatSupportBand() {
  return (
    <div className="tw-flex tw-shrink-0 tw-items-start tw-gap-3 tw-border-b tw-border-border tw-bg-canvas-accent tw-p-4 desktop:tw-hidden">
      <span className="tw-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-brand-black tw-text-white">
        <MessagesSquare className="tw-size-5" />
      </span>
      <div className="tw-min-w-0 tw-flex-1">
        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-leading-5 tw-text-brand-black">
          <FormattedMessage id="chat.support_banner.title" defaultMessage="Support team" />
        </p>
        <p className="tw-m-0 tw-mt-0.5 tw-text-[13px] tw-leading-[18px] tw-text-[#6B7280]">
          <FormattedMessage id="chat.support_banner.desc" defaultMessage="We're online and happy to help! We answer during business hours: weekdays, 9:00–18:00 (MSK)" />
        </p>
      </div>
    </div>
  );
}

function ChatThreadsList({ threads, selectedThreadId, onSelect }: { threads: ThreadRow[]; selectedThreadId: number | null; onSelect: (id: number) => void }) {
  const intl = useIntl();

  return (
    // The ONLY scroller of the list column — it owns its own `px-4` so its vertical scrollbar never affects the
    // full-width support band (a sibling above it). Wrappers therefore carry NO horizontal padding.
    <div className="rd-chat-ground tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-gap-2 tw-overflow-y-auto tw-bg-white tw-p-4 max-[990px]:tw-pb-[calc(4.5rem+env(safe-area-inset-bottom))]">
      {threads.map((t) => (
        <button
          key={t.id}
          type="button"
          className={clsx(
            "tw-box-border tw-flex tw-h-auto tw-min-h-[unset] tw-w-full tw-grow-0 tw-basis-auto tw-appearance-none tw-flex-col tw-items-stretch tw-justify-start tw-overflow-visible tw-border-0 tw-p-4 tw-text-left tw-transition-[background-color,box-shadow]",
            "tw-outline-none focus:tw-outline-none focus-visible:tw-outline-none",
            "tw-ring-0 tw-ring-offset-0 focus:tw-ring-0 focus-visible:tw-ring-0 active:tw-border-0",
            // Selected thread = stone-200 #e7e5e4 (sampled from Figma 7221-19716), UNIFORM across PC / mobile
            // / widget. Others transparent with a faint stone hover.
            selectedThreadId === t.id ? "tw-rounded-lg tw-bg-[#e7e5e4]" : "tw-rounded-lg tw-bg-transparent hover:tw-bg-[#f5f5f4] desktop-lg:hover:tw-bg-[#f5f5f4]",
          )}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onSelect(t.id)}
        >
          <div className="tw-flex tw-w-full tw-shrink-0 tw-items-start tw-gap-4">
            <ThreadListAvatar channel={t.channel} assignee={t.assignee ?? null} size="md" />
            <div className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-justify-center tw-gap-0.5 tw-overflow-hidden">
              <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-medium tw-leading-5 tw-text-brand-black">
                  <FormattedMessage id={channelTitleId(t.channel)} />
                </span>
                <span className="tw-shrink-0 tw-text-xs tw-tabular-nums tw-leading-4 tw-text-[#737373]">{formatChatThreadListTime(t.last_message?.created_at ?? null, intl.locale)}</span>
              </div>
              <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                <span className="tw-line-clamp-2 tw-min-w-0 tw-flex-1 tw-text-left tw-text-sm tw-font-normal tw-leading-5 tw-text-brand-black">{threadPreviewText(t, intl)}</span>
                {t.unread_count > 0 ? (
                  <span className="tw-ml-1 tw-flex tw-h-5 tw-min-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary tw-px-1 tw-text-[11px] tw-font-bold tw-text-primary-foreground">
                    {t.unread_count > 99 ? "99+" : t.unread_count}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function ChatThreadView({
  threadId,
  channel,
  assignee,
  threadListHadMessage,
  activeTab,
  onActiveTabChange,
  threadSearchQuery,
  onThreadSearchQueryChange,
  onThreadSearchResultCount,
  hideOwnFilesHeader = false,
  onMediaFilesCount,
}: {
  threadId: number;
  channel: TChannel;
  assignee: ThreadRow["assignee"];
  /** True if thread list reported a last message (history exists before messages query resolves). */
  threadListHadMessage: boolean;
  activeTab: "chat" | "media";
  onActiveTabChange: (t: "chat" | "media") => void;
  threadSearchQuery: string;
  onThreadSearchQueryChange: (value: string) => void;
  onThreadSearchResultCount: (count: number | null) => void;
  /** Hides the internal Files header when the desktop grid supplies the shared header row. */
  hideOwnFilesHeader?: boolean;
  onMediaFilesCount?: (count: number) => void;
}) {
  const intl = useIntl();
  const user = useOptionalUser();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composeLocked = isStaffChannelComposeLocked(channel, assignee);
  const threadSearchNormalized = normalizeChatThreadSearchQuery(threadSearchQuery);
  const threadSearchActive = threadSearchNormalized.length > 0;

  const { data, isLoading } = useGetChatMessagesQuery(
    { threadId, limit: 50 },
    {
      skip: activeTab !== "chat" || threadSearchActive,
      pollingInterval: activeTab === "chat" && !threadSearchActive ? 3500 : 0,
    },
  );
  const { messages: searchMessages, isLoading: isSearchMessagesLoading } = useChatThreadAllMessages(threadId, activeTab === "chat" && threadSearchActive);

  const messages = threadSearchActive ? (searchMessages ?? []) : (data?.messages ?? []);
  const showAssigneeRemovedNotice = composeLocked && (messages.length > 0 || threadListHadMessage);

  const [sendChatMessage, sendState] = useSendChatMessageMutation();
  const [markRead] = useMarkChatReadMutation();

  const doctorAccountId = user?.account_id ?? 0;
  const [draft, setDraft] = useState(() => readChatComposeDraft(doctorAccountId || undefined, threadId));
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const composeTextareaRef = useChatComposeAutogrow(draft, 120);
  const [attachError, setAttachError] = useState<string | null>(null);

  const mediaQuery = useGetChatMediaQuery({ threadId }, { skip: activeTab !== "media" && !threadSearchActive });

  const filteredMessages = useMemo(
    () => (threadSearchActive ? messages.filter((m) => chatMessageMatchesThreadSearch(m, threadSearchNormalized)) : messages),
    [messages, threadSearchActive, threadSearchNormalized],
  );

  const filteredMediaFiles = useMemo(() => {
    const files = mediaQuery.data?.files ?? [];
    return threadSearchActive ? files.filter((f) => chatFileMatchesThreadSearch(f, threadSearchNormalized)) : files;
  }, [mediaQuery.data?.files, threadSearchActive, threadSearchNormalized]);

  const searchResultCount = activeTab === "media" ? filteredMediaFiles.length : filteredMessages.length;

  useEffect(() => {
    onThreadSearchResultCount(threadSearchActive ? searchResultCount : null);
  }, [onThreadSearchResultCount, searchResultCount, threadSearchActive]);

  useEffect(() => {
    onMediaFilesCount?.(filteredMediaFiles.length);
  }, [onMediaFilesCount, filteredMediaFiles.length]);

  const { listRef, onScroll, pinToBottom } = useChatMessagesAutoScroll(threadId, filteredMessages, activeTab === "chat" && !threadSearchActive);

  useEffect(() => {
    setDraft(readChatComposeDraft(doctorAccountId || undefined, threadId));
    setAttachError(null);
    onThreadSearchQueryChange("");
  }, [doctorAccountId, onThreadSearchQueryChange, threadId]);

  useEffect(() => {
    if (!doctorAccountId) {
      return;
    }
    const timer = window.setTimeout(() => {
      writeChatComposeDraft(doctorAccountId, threadId, draftRef.current);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [doctorAccountId, threadId, draft]);

  useEffect(() => {
    if (!doctorAccountId) {
      return;
    }
    return () => {
      writeChatComposeDraft(doctorAccountId, threadId, draftRef.current);
    };
  }, [doctorAccountId, threadId]);

  async function onSend() {
    if (composeLocked) return;
    const text = draft.trim();
    if (!text) return;
    await sendChatMessage({ threadId, text }).unwrap();
    clearChatComposeDraft(doctorAccountId || undefined, threadId);
    setDraft("");
    pinToBottom();
  }

  React.useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last) return;
    void markRead({ threadId, lastMessageId: last.id });
  }, [messages, markRead, threadId]);

  async function attachFiles(files: File[]) {
    if (composeLocked) return;
    setAttachError(null);
    if (files.length === 0) return;
    const list = files.slice(0, 10);
    const form = new FormData();
    for (const f of list) form.append("files", f);

    const res = await fetchWithAuth(`${process.env.API}/chat/threads/${threadId}/uploads`, {
      method: "POST",
      body: form,
    });
    if (!res.ok) {
      let msg = intl.formatMessage({ id: "chat.upload.error" });
      try {
        const err = (await res.json()) as { message?: string; description?: string };
        msg = err.description || err.message || msg;
      } catch {
        /* ignore */
      }
      setAttachError(msg);
      return;
    }
    const payload = (await res.json()) as { files?: { id: number }[] };
    const fileIds = (payload.files ?? []).map((f) => f.id);
    if (fileIds.length) {
      await sendChatMessage({ threadId, fileIds }).unwrap();
      pinToBottom();
    }
    onActiveTabChange("chat");
  }

  async function onAttach(files: FileList | null) {
    if (!files || files.length === 0) return;
    await attachFiles(Array.from(files));
  }

  const doctorAvatar = user?.avatar ?? undefined;
  const doctorInitials = user ? initialsFromPerson(user.last_name, user.first_name, user.middle_name) || "?" : "?";

  return (
    <div className="rd-chat-ground tw-flex tw-h-full tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-bg-white">
      {activeTab === "media" ? (
        <ChatFileDropZone className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col" disabled={composeLocked} onDropFiles={(files) => void attachFiles(files)}>
          {/* Mobile header; the desktop grid supplies a shared header row. */}
          {!hideOwnFilesHeader ? (
            <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-3 tw-border-b tw-border-[#E1E4ED] tw-bg-[#f5f5f5] tw-p-4">
              <div className="tw-flex tw-items-center tw-gap-2">
                <button
                  type="button"
                  onClick={() => onActiveTabChange("chat")}
                  aria-label={intl.formatMessage({ id: "reminder.back" })}
                  className="tw-flex tw-size-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-p-0 tw-text-brand-black hover:tw-bg-gray-100"
                >
                  {/* Remove native button padding so the 24px icon does not shrink. */}
                  <ChevronLeft className="tw-size-6 tw-shrink-0" />
                </button>
                <div className="tw-flex tw-min-w-0 tw-flex-col">
                  <span className="tw-text-sm tw-font-semibold tw-text-brand-black">
                    <FormattedMessage id="chat.files.title" defaultMessage="Chat files" />
                  </span>
                  <span className="tw-text-xs tw-text-[#6B7280]">
                    <FormattedMessage id="chat.files.count" defaultMessage="{count, plural, one {# file} other {# files}}" values={{ count: filteredMediaFiles.length }} />
                  </span>
                </div>
              </div>
              <div className="tw-pl-8">
                <SearchInput
                  value={threadSearchQuery}
                  onChange={(e) => onThreadSearchQueryChange(e.currentTarget.value)}
                  onClear={() => onThreadSearchQueryChange("")}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") onThreadSearchQueryChange("");
                  }}
                  placeholder={intl.formatMessage({ id: "chat.thread.search.placeholder" })}
                  clearAriaLabel={intl.formatMessage({ id: "chat.thread.search.clear" })}
                  aria-label={intl.formatMessage({ id: "chat.thread.search.placeholder" })}
                />
              </div>
            </div>
          ) : null}
          <div className="rd-chat-ground tw-min-h-0 tw-flex-1 tw-overflow-auto tw-bg-white tw-px-2 tw-py-3 max-[990px]:tw-pb-[calc(4.5rem+env(safe-area-inset-bottom))] desktop:tw-p-4">
            {mediaQuery.isLoading ? <Loader /> : null}
            {threadSearchActive && !mediaQuery.isLoading && filteredMediaFiles.length === 0 ? (
              <p className="tw-m-0 tw-text-center tw-text-sm tw-text-[#6B7280]">
                <FormattedMessage id="chat.thread.search.empty" />
              </p>
            ) : null}
            {!threadSearchActive && !mediaQuery.isLoading && filteredMediaFiles.length === 0 ? (
              <p className="tw-m-0 tw-text-center tw-text-sm tw-text-[#6B7280]">
                <FormattedMessage id="chat.files.empty" defaultMessage="No files yet" />
              </p>
            ) : null}
            {/* Group the files by month with a centred month pill (frame's "Июль" / "Май" separators). */}
            {groupChatFilesByMonth(filteredMediaFiles).map((group) => (
              <div key={group.key} className="tw-mb-4">
                <ChatDateSeparator label={formatChatFileMonth(group.files[0]?.created_at, intl.locale)} />
                {/* Mobile cards keep a fixed width; desktop cards fill available columns from 260px. */}
                <div className="tw-mt-3 tw-grid tw-grid-cols-[repeat(auto-fill,118px)] tw-gap-1 desktop:tw-w-full desktop:tw-grid-cols-[repeat(auto-fill,minmax(260px,1fr))] desktop:tw-gap-4">
                  {group.files.map((f) => {
                    const kind = chatFileKind(f.mime_type, f.file_name);
                    const url = resolveChatFileUrl(f.get_url);
                    // Fixed heights keep file names from changing the thumbnail grid geometry.
                    const cardCls = "tw-flex tw-h-[6.75rem] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-[#E4E4E4] tw-bg-white desktop:tw-h-auto desktop:tw-min-h-[12.0625rem]";
                    const nameCls =
                      "tw-block tw-min-w-0 tw-shrink-0 tw-truncate tw-px-2 tw-py-1.5 tw-text-xs tw-text-brand-black hover:tw-underline desktop:tw-line-clamp-2 desktop:tw-whitespace-normal";
                    // Thumbnail area: mobile FILLS the 108px card (flex-1); PC is a FIXED 152px band (flex-none)
                    // so `object-cover` clips tall/square images instead of the card growing to the image ratio
                    // (the too-tall-cards bug). Card min-h 193 = 152 band + the ~41px file-name row.
                    const thumbCls = "tw-min-h-0 tw-w-full tw-flex-1 tw-overflow-hidden desktop:tw-h-[9.5rem] desktop:tw-flex-none";
                    if (kind === "video" && url) {
                      return (
                        <div key={f.id} className={cardCls}>
                          <div className={clsx(thumbCls, "tw-relative")}>
                            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- chat file attachment */}
                            <video className="tw-size-full tw-bg-black tw-object-cover" controls preload="metadata" playsInline src={url} />
                            <ChatVideoExpandButton src={url} alt={f.file_name} />
                          </div>
                          <a href={url} target="_blank" rel="noreferrer" className={nameCls}>
                            {f.file_name}
                          </a>
                        </div>
                      );
                    }
                    return (
                      <div key={f.id} className={cardCls}>
                        <div className={thumbCls}>
                          <ChatFileThumbnail fileName={f.file_name} mimeType={f.mime_type} url={url} variant="grid" />
                        </div>
                        <a href={url} target="_blank" rel="noreferrer" className={nameCls}>
                          {f.file_name}
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </ChatFileDropZone>
      ) : (
        <>
          <ChatFileDropZone className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col" disabled={composeLocked} onDropFiles={(files) => void attachFiles(files)}>
            <div ref={listRef} onScroll={onScroll} className="rd-chat-ground tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-3 tw-overflow-y-auto tw-bg-white tw-p-4">
              {(isLoading || isSearchMessagesLoading) && filteredMessages.length === 0 ? <Loader /> : null}
              {threadSearchActive && !isLoading && !isSearchMessagesLoading && filteredMessages.length === 0 ? (
                <p className="tw-m-0 tw-text-center tw-text-sm tw-text-[#6B7280]">
                  <FormattedMessage id="chat.thread.search.empty" />
                </p>
              ) : null}
              {filteredMessages.map((m, index) => {
                const dayKey = messageDayKey(m.created_at);
                const prevDayKey = index > 0 ? messageDayKey(filteredMessages[index - 1]?.created_at) : null;
                const showDateSep = Boolean(dayKey && dayKey !== prevDayKey);
                return (
                  <React.Fragment key={m.id}>
                    {showDateSep ? <ChatDateSeparator label={formatChatDateSeparator(m.created_at, intl.locale)} /> : null}
                    <MessageBubble message={m} channel={channel} assignee={assignee} locale={intl.locale} doctorAccountId={doctorAccountId} doctorAvatar={doctorAvatar} doctorInitials={doctorInitials} tone="default" />
                  </React.Fragment>
                );
              })}
            </div>
          </ChatFileDropZone>

          <div className="tw-h-px tw-w-full tw-shrink-0 tw-bg-[#E1E4ED]" aria-hidden />

          {/* Mobile: the white surface runs to the viewport bottom (behind the FIXED tab bar), so the compose
              clears the bar with a clean gap instead of a grey canvas strip — pb = bar height (~3.5rem) + safe
              area + a 1rem gap. Desktop keeps the plain p-4. */}
          <div className="rd-chat-ground tw-flex tw-shrink-0 tw-flex-col tw-gap-2 tw-bg-white tw-p-4 max-[990px]:tw-pb-[calc(4.5rem+env(safe-area-inset-bottom))]">
            {showAssigneeRemovedNotice ? (
              <div
                className="tw-whitespace-pre-line tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-[11px] tw-font-medium tw-leading-snug tw-text-amber-950"
                role="status"
              >
                <FormattedMessage id={channel === "personal_manager" ? "chat.readonly.no_assignee_notice_manager" : "chat.readonly.no_assignee_notice"} />
              </div>
            ) : null}
            {attachError ? <div className="tw-text-xs tw-text-red-600">{attachError}</div> : null}
            {/* Bottom alignment keeps the controls fixed as the textarea grows upward. */}
            <div className="tw-flex tw-items-end tw-gap-2">
              <input ref={fileInputRef} type="file" className="tw-sr-only" multiple onChange={(e) => void onAttach(e.target.files)} />
              <button
                type="button"
                className="tw-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-brand-gray tw-shadow-none tw-outline-none tw-ring-0 tw-ring-offset-0 tw-transition-colors hover:tw-bg-transparent hover:tw-text-brand-black focus:tw-outline-none focus-visible:tw-ring-0 active:tw-opacity-70 disabled:tw-cursor-not-allowed disabled:tw-opacity-40 disabled:hover:tw-bg-transparent disabled:hover:tw-text-brand-gray"
                title={intl.formatMessage({ id: "chat.attach" })}
                aria-label={intl.formatMessage({ id: "chat.attach" })}
                disabled={composeLocked}
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="tw-size-6 tw-shrink-0" />
              </button>
              <textarea
                ref={composeTextareaRef}
                rows={1}
                // Long unbroken text must wrap inside the composer.
                className="tw-placeholder:text-black/30 tw-max-h-[7.5rem] tw-min-h-10 tw-min-w-0 tw-flex-1 tw-resize-none tw-overflow-y-auto tw-break-all tw-rounded-lg tw-border tw-border-black/10 tw-bg-white tw-px-4 tw-py-2.5 tw-text-sm tw-leading-5 tw-text-black/85 tw-outline-none focus:tw-border-black/20 disabled:tw-cursor-not-allowed disabled:tw-bg-[#F3F4F6] disabled:tw-text-black/40"
                placeholder={intl.formatMessage({ id: "chat.message.placeholder" })}
                value={draft}
                disabled={composeLocked}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void onSend();
                  }
                  // Shift+Enter falls through to the textarea's native newline insertion.
                }}
              />
              <button
                type="button"
                className="chat-compose-send-btn tw-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-shadow-none tw-outline-none tw-ring-0 tw-ring-offset-0 focus:tw-outline-none focus:tw-ring-0 focus-visible:tw-outline-none focus-visible:tw-ring-0 disabled:tw-cursor-not-allowed disabled:tw-opacity-40"
                style={{ outline: "none", border: "none", boxShadow: "none" }}
                aria-label={intl.formatMessage({ id: "chat.send" })}
                disabled={composeLocked || sendState.isLoading || !draft.trim()}
                onClick={() => void onSend()}
              >
                <Send className="tw-size-4 tw-shrink-0" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A message-bubble image attachment. If the image fails to load (deleted / not found), a broken `<img>` would
 * render its `alt` (the full file name) unwrapped and blow the bubble width out → horizontal scroll. So on
 * `onError` falls back to the file-icon row, whose file name wraps within the bubble.
 */
function ChatBubbleImage({ url, fileName, mimeType, fileRowCls }: { url: string; fileName: string; mimeType: string | null; fileRowCls: string }) {
  const [errored, setErrored] = useState(false);

  if (errored) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className={clsx(fileRowCls, "tw-max-w-[280px] tw-gap-2")}>
        <ChatFileThumbnail fileName={fileName} mimeType={mimeType} url={undefined} variant="inline" />
        <span className="tw-line-clamp-2 tw-min-w-0 tw-flex-1 tw-break-words tw-text-xs tw-font-medium tw-leading-[18px]">{fileName}</span>
      </a>
    );
  }

  return (
    <div className="tw-max-w-[280px]">
      <ChatImagePreviewTrigger src={url} alt={fileName} className="tw-block tw-overflow-hidden tw-rounded-lg">
        <img src={url} alt={fileName} onError={() => setErrored(true)} className="tw-max-h-[220px] tw-w-full tw-object-cover" loading="lazy" crossOrigin="use-credentials" />
      </ChatImagePreviewTrigger>
      <a href={url} target="_blank" rel="noreferrer" className="tw-mt-1 tw-block tw-break-words tw-text-xs tw-font-medium tw-text-brand-black hover:tw-underline">
        {fileName}
      </a>
    </div>
  );
}

export function MessageBubble({
  message,
  channel,
  assignee,
  locale,
  doctorAccountId,
  doctorAvatar,
  doctorInitials,
  tone = "default",
}: {
  message: {
    sender_type: "doctor" | "lab";
    text: string;
    created_at: string | null;
    files: { file_name: string; get_url: string | null; mime_type: string | null }[];
    sender_account_id?: number;
    sender_avatar?: string | null;
    sender_first_name?: string;
    sender_last_name?: string;
    sender_middle_name?: string;
  };
  /** Used for the channel avatar when the message has no resolvable sender or assignee. */
  channel?: TChannel;
  assignee?: ThreadRow["assignee"];
  locale: string;
  doctorAccountId: number;
  doctorAvatar: string | null | undefined;
  doctorInitials: string;
  tone?: "default" | "sheet";
}) {
  const isMine = message.sender_type === "doctor";
  const time = formatBubbleTime(message.created_at, locale);

  const labAvatar = resolveLabMessageAvatar(message, assignee ?? null);

  const sideAvatar = isMine ? (
    doctorAccountId > 0 ? (
      <ChatRoundAvatar accountId={doctorAccountId} avatarFilename={doctorAvatar} initials={doctorInitials} size="md" />
    ) : (
      <div className="tw-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-[#E8EAED] tw-bg-white tw-text-[10px] tw-font-semibold tw-text-brand-black">
        {doctorInitials}
      </div>
    )
  ) : labAvatar ? (
    <ChatRoundAvatar accountId={labAvatar.accountId} avatarFilename={labAvatar.avatarFilename} initials={labAvatar.initials} size="md" />
  ) : channel ? (
    // Use the same channel fallback as the thread list for unattributed messages.
    <ThreadListAvatar channel={channel} assignee={assignee ?? null} size="md" />
  ) : (
    <div className="tw-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-[#E8EAED] tw-bg-white tw-text-[10px] tw-font-semibold tw-text-brand-gray">?</div>
  );

  const bubbleBody = (
    <div
      className={clsx(
        "tw-flex tw-max-w-[min(100%,500px)] tw-flex-col tw-gap-2 tw-p-4",
        // The floating widget adds a shadow; the page uses flat message bubbles.
        tone === "sheet"
          ? isMine
            ? "tw-rounded-[12px_0_12px_12px] tw-bg-[#ffedd5] tw-shadow-sm"
            : "tw-rounded-[0_12px_12px_12px] tw-bg-white tw-shadow-sm"
          : isMine
            ? "tw-rounded-[12px_0_12px_12px] tw-bg-[#FFEDD5]"
            : "tw-rounded-[0_12px_12px_12px] tw-bg-[#F5F5F5]",
      )}
    >
      {message.text ? (
        <div className={clsx("tw-whitespace-pre-wrap tw-break-words tw-text-sm tw-font-normal tw-leading-5", tone === "sheet" ? "tw-text-[#0a0a0a]" : "tw-text-brand-black")}>{message.text}</div>
      ) : null}
      {message.files?.length ? (
        <div className="tw-mt-1 tw-flex tw-flex-col tw-gap-2">
          {message.files.map((f) => {
            const kind = chatFileKind(f.mime_type, f.file_name);
            const url = resolveChatFileUrl(f.get_url);
            // Limit attachment width so an unbroken file name cannot widen the message bubble.
            const fileRowCls = "tw-flex tw-min-w-0 tw-max-w-[280px] tw-items-start tw-gap-1.5 tw-rounded-lg tw-border tw-border-[#E4E4E4] tw-bg-[#E8E8E8] tw-p-2 tw-text-brand-black";
            if (kind === "image" && url) {
              return <ChatBubbleImage key={`${f.file_name}-${f.get_url}`} url={url} fileName={f.file_name} mimeType={f.mime_type} fileRowCls={fileRowCls} />;
            }
            if (kind === "video" && url) {
              return (
                <div key={`${f.file_name}-${f.get_url}`} className="tw-max-w-xs">
                  <div className="tw-relative">
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption -- chat file attachment */}
                    <video className="tw-max-h-60 tw-w-full tw-rounded-lg tw-bg-black" controls preload="metadata" playsInline src={url} crossOrigin="use-credentials" />
                    <ChatVideoExpandButton src={url} alt={f.file_name} />
                  </div>
                  <a href={url} target="_blank" rel="noreferrer" className="tw-mt-1 tw-block tw-break-words tw-text-xs tw-font-medium tw-text-brand-black hover:tw-underline">
                    {f.file_name}
                  </a>
                </div>
              );
            }
            return (
              <a key={`${f.file_name}-${f.get_url}`} href={url} target="_blank" rel="noreferrer" className={clsx(fileRowCls, "tw-gap-2")}>
                <ChatFileThumbnail fileName={f.file_name} mimeType={f.mime_type} url={url} variant="inline" />
                <span className="tw-line-clamp-2 tw-min-w-0 tw-flex-1 tw-break-words tw-text-xs tw-font-medium tw-leading-[18px]">{f.file_name}</span>
              </a>
            );
          })}
        </div>
      ) : null}
      {time ? <div className="tw-text-xs tw-font-normal tw-leading-4 tw-text-[#737373]">{time}</div> : null}
    </div>
  );

  if (isMine) {
    return (
      <div className="tw-flex tw-w-full tw-justify-end">
        <div className="tw-flex tw-max-w-[90%] tw-flex-row-reverse tw-items-start tw-gap-3">
          {sideAvatar}
          {bubbleBody}
        </div>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-w-full tw-justify-start">
      <div className="tw-flex tw-max-w-[90%] tw-items-start tw-gap-3">
        {sideAvatar}
        {bubbleBody}
      </div>
    </div>
  );
}
